import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { Vehicle } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * スケジュール矯正リセットAPI
 */
export const useUnsafeResetAPI = (): {
  state: APIState;
  postUnsafeResetVehicles: (vehicles: Vehicle[]) => Promise<(boolean | null)[]>;
  postUnsafeResetSingleVehicle: (
    vehicle: Vehicle,
    isSchedulePage?: boolean,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postUnsafeResetAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/unsafe_reset`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postUnsafeResetVehicles = useAtomCallback(
    useCallback(
      async (get, _, vehicles: Vehicle[]) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({ message: t('api.fms.post_unsafe_reset.all.failed') });
          setState('hasError');
          return [];
        }
        setState('loading');
        const responses = await Promise.all(
          vehicles.map(async (vehicle: Vehicle) => {
            const res = await postUnsafeResetAPI(
              project!.id,
              environment!.environment_id,
              vehicle.vehicle_id,
            );
            if (isNullOrUndefined(res.value) || res.isFailure()) {
              notifyError({
                message: `${t('api.fms.post_unsafe_reset.vehicle.failed', {
                  name: vehicle.vehicle_name,
                })}: ${getErrorMessage(res)}`,
              });
              return null;
            }
            notifySuccess({
              message: `${t('api.fms.post_unsafe_reset.vehicle.successfully', {
                name: vehicle.vehicle_name,
              })}${t('api.fms.post_unsafe_reset.check_schedule_list_page')}`,
              testIdAttrs: {
                wrapperTestId: 'schedulesReset-snackbar',
                closeButtonTestId: 'schedulesReset-snackbar-close-btn',
              },
            });
            return res.value;
          }),
        );
        setState('hasValue');
        return responses;
      },
      [notifyError, notifySuccess, postUnsafeResetAPI, t, getErrorMessage],
    ),
  );

  const postUnsafeResetSingleVehicle = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicle: Vehicle,
        isSchedulePage: boolean | undefined = false,
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_unsafe_reset.vehicle.failed', {
              name: vehicle.vehicle_name,
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await postUnsafeResetAPI(
          project.id,
          environment.environment_id,
          vehicle.vehicle_id,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_unsafe_reset.vehicle.failed', {
              name: vehicle.vehicle_name,
            })}: ${getErrorMessage(res)}`,
          });
          return false;
        }
        notifySuccess({
          message: `${t('api.fms.post_unsafe_reset.vehicle.successfully', {
            name: vehicle.vehicle_name,
          })}${
            isSchedulePage
              ? ''
              : t('api.fms.post_unsafe_reset.check_schedule_list_page')
          }`,
          testIdAttrs: {
            wrapperTestId: 'scheduleReset-snackbar',
            closeButtonTestId: 'scheduleReset-snackbar-close-btn',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, postUnsafeResetAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postUnsafeResetVehicles,
    postUnsafeResetSingleVehicle,
  };
};
